import { Text } from '@ltvco/refresh-lib/theme';
import { commaFormatCurrency, useScreenSize } from '@ltvco/refresh-lib/utils';
import { AutoAwesomeOutlined } from '@mui/icons-material';
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import {
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type XAxisTickContentProps,
} from 'recharts';

import { theme } from 'theme';

import {
  Card,
  ChartContainer,
  Container,
  FixedMobileTooltip,
  Header,
  SummaryCards,
} from '../components';
import {
  calculateChartDomain,
  calculateDynamicTicks,
  calculateTooltipPosition,
  calculateVehicleForecasterValues,
  formatCurrencyValueChange,
  formatYAxis,
} from '../utils';

import { CustomChartDot } from './CustomChartDot';
import { CustomTooltipComponent } from './CustomTooltipComponent';

import type { VehicleForecast } from '@ltvco/refresh-lib/api';

export interface IValueForecasterResultCardProps {
  forecastData?: VehicleForecast;
  marketValue: number;
  onTooltipOpenChange?: (isOpen: boolean) => void;
}

export interface IQuarterlyData {
  quarter: string;
  value: number;
  change_percent: number;
  index: number;
}

/**
 * ValueForecasterResultCard - Displays 3-year vehicle value forecast with interactive chart
 * @component
 */
export const ValueForecasterResultCard: React.FC<
  IValueForecasterResultCardProps
> = ({ forecastData, marketValue, onTooltipOpenChange }) => {
  const { isMobile } = useScreenSize();
  const [selectedPointIndex, setSelectedPointIndex] = useState<number | null>(
    null,
  );
  const [fixedPosition, setFixedPosition] = useState<{
    left: number;
    top: number;
  } | null>(null);
  const containerNodeRef = useRef<HTMLDivElement | null>(null);

  // Calculate dollar values from percentages - safe with optional chaining
  const forecastWithValues = useMemo(() => {
    if (!forecastData) {
      return null;
    }
    return calculateVehicleForecasterValues(forecastData, marketValue);
  }, [forecastData, marketValue]);

  const quarterlyData: IQuarterlyData[] = useMemo(() => {
    if (!forecastWithValues?.trend_by_quarter) {
      return [];
    }
    return forecastWithValues.trend_by_quarter.map((d, index) => ({
      quarter: d.quarter,
      value: d.value,
      change_percent: d.change_percent,
      index,
    }));
  }, [forecastWithValues]);

  // Calculate dynamic domain and ticks based on data values
  const chartDomain = useMemo(() => {
    if (quarterlyData.length === 0) {
      return { domainMin: 0, domainMax: 100, minValue: 0, maxValue: 100 };
    }
    const values = quarterlyData.map((d) => d.value);
    return calculateChartDomain(values);
  }, [quarterlyData]);

  const { domainMin, domainMax, minValue, maxValue } = chartDomain;

  const uniqueTicks = useMemo(
    () => calculateDynamicTicks(domainMin, domainMax, isMobile),
    [domainMin, domainMax, isMobile],
  );

  const renderCustomXAxisTick = useCallback(
    (props: XAxisTickContentProps) => {
      const { x, y, index } = props;
      if (!quarterlyData[index]) return null;

      const quarterParts = quarterlyData[index].quarter.split(' ');
      const year = quarterParts[0];
      const quarter = quarterParts[1] ?? quarterParts[0];

      // Show year when it's the first tick or when the year changes from previous tick
      const prevYear =
        index > 0 ? quarterlyData[index - 1]?.quarter.split(' ')[0] : null;
      const showYear = index === 0 || (year !== prevYear && !isMobile);

      const label = showYear ? `${year} ${quarter}` : quarter;

      return (
        <text
          x={Number(x)}
          y={Number(y) + (isMobile ? 15 : 20)}
          textAnchor="middle"
          fill={theme.palette.text.secondary}
          fontSize={isMobile ? '0.5825rem' : 14}
        >
          {label}
        </text>
      );
    },
    [quarterlyData, isMobile],
  );

  const handleChartContainerClick = useCallback(() => {
    if (isMobile && selectedPointIndex !== null) {
      setSelectedPointIndex(null);
    }
  }, [isMobile, selectedPointIndex]);

  // Custom dot component for mobile with click handling
  const renderCustomDot = useCallback(
    (props: any) => (
      <CustomChartDot
        {...props}
        setSelectedPointIndex={setSelectedPointIndex}
      />
    ),
    [setSelectedPointIndex],
  );

  const tooltipPosition = useMemo(
    () =>
      selectedPointIndex !== null && quarterlyData[selectedPointIndex]
        ? calculateTooltipPosition(
            selectedPointIndex,
            quarterlyData.length,
            quarterlyData[selectedPointIndex].value,
            minValue,
            maxValue,
          )
        : { left: 50, top: 50 },
    [selectedPointIndex, quarterlyData, minValue, maxValue],
  );

  // Callback ref to store node
  const chartContainerRef = useCallback((node: HTMLDivElement | null) => {
    containerNodeRef.current = node;
  }, []);

  // Calculate position when dependencies change
  useEffect(() => {
    const isOpen = isMobile && selectedPointIndex !== null;
    onTooltipOpenChange?.(isOpen);

    if (isOpen && containerNodeRef.current) {
      const rect = containerNodeRef.current.getBoundingClientRect();
      const left = rect.left + (rect.width * tooltipPosition.left) / 100;
      const topPercent = tooltipPosition.top;
      const adjustedTop = Math.max(
        30,
        topPercent > 70 ? topPercent - 10 : topPercent,
      );
      const top = rect.top + (rect.height * adjustedTop) / 100;
      setFixedPosition({ left, top });
    } else {
      setFixedPosition(null);
    }
  }, [selectedPointIndex, tooltipPosition, isMobile, onTooltipOpenChange]);

  if (
    !forecastData ||
    !forecastData.trend_by_quarter ||
    forecastData['12_months_change'] === undefined ||
    forecastData['36_months_change'] === undefined ||
    !forecastWithValues
  ) {
    return null;
  }

  return (
    <Container width="100%">
      <Header p={{ xs: 1.25, md: 0 }}>
        <AutoAwesomeOutlined sx={{ color: theme.palette.primary.main }} />
        <Text variant="h3">3 Year Value Forecast</Text>
      </Header>
      <SummaryCards>
        <Card>
          <Text variant="body1" mb={1} color="text.secondary">
            Value change in 12 mths
          </Text>
          <Text variant="h1" fontWeight="bold" color="error.main">
            {formatCurrencyValueChange(
              marketValue,
              forecastWithValues['12_months_change'],
            )}
          </Text>
        </Card>
        <Card>
          <Text variant="body1" mb={1} color="text.secondary">
            Value change in 36 mths
          </Text>
          <Text variant="h1" fontWeight="bold" color="error.main">
            {formatCurrencyValueChange(
              marketValue,
              forecastWithValues['36_months_change'],
            )}
          </Text>
        </Card>
      </SummaryCards>

      <ChartContainer
        ref={chartContainerRef}
        onClick={handleChartContainerClick}
      >
        {isMobile &&
          selectedPointIndex !== null &&
          quarterlyData[selectedPointIndex] &&
          fixedPosition && (
            <FixedMobileTooltip
              left={fixedPosition.left}
              top={fixedPosition.top}
            >
              <Text variant="caption" fontWeight="bold" mb={0.25}>
                {quarterlyData[selectedPointIndex].quarter}
              </Text>
              <Text variant="body1" color="text.secondary" mb={0.25}>
                {commaFormatCurrency(quarterlyData[selectedPointIndex].value)}
              </Text>
              <Text fontSize="0.6875rem" color="error.main" mb={0.25}>
                {quarterlyData[selectedPointIndex].change_percent}%
              </Text>
            </FixedMobileTooltip>
          )}
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={quarterlyData}
            margin={
              isMobile
                ? { top: 40, right: 20, left: 0, bottom: 10 }
                : { top: 60, right: 50, left: 20, bottom: 20 }
            }
          >
            <XAxis
              dataKey="index"
              axisLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
              tickLine={false}
              tick={renderCustomXAxisTick}
              type="number"
              domain={[0, quarterlyData.length - 1]}
              ticks={quarterlyData.map((_, i) => i)}
            />
            <YAxis
              axisLine={{ stroke: theme.palette.divider, strokeWidth: 1 }}
              tickLine={false}
              tick={{
                fill: theme.palette.text.secondary,
                fontSize: isMobile ? '0.5825rem' : 14,
              }}
              tickFormatter={formatYAxis}
              domain={[domainMin, domainMax]}
              ticks={uniqueTicks}
              width={isMobile ? 35 : 60}
            />
            {!isMobile && (
              <Tooltip
                content={<CustomTooltipComponent />}
                cursor={{
                  stroke: theme.palette.text.secondary,
                  strokeWidth: 1,
                  strokeDasharray: '5 5',
                }}
                position={{ x: undefined, y: undefined }}
                allowEscapeViewBox={{ x: true, y: true }}
                wrapperStyle={{
                  zIndex: 1000,
                  pointerEvents: 'none',
                }}
              />
            )}
            <Line
              type="monotone"
              dataKey="value"
              stroke={theme.palette.primary.main}
              strokeWidth={isMobile ? 2 : 3}
              dot={
                isMobile
                  ? renderCustomDot
                  : {
                      r: 6,
                      fill: theme.palette.primary.main,
                      stroke: 'white',
                      strokeWidth: 2,
                    }
              }
              activeDot={
                isMobile
                  ? false
                  : {
                      r: 8,
                      fill: theme.palette.primary.main,
                      stroke: 'white',
                      strokeWidth: 2,
                      style: {
                        cursor: 'default',
                        filter: `drop-shadow(0 2px 4px ${theme.palette.primary.main})`,
                      },
                    }
              }
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartContainer>
    </Container>
  );
};
